import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { setPageTitle } from '../../utils/pageTitle';
import { Image, List, PlusCircle, Home, ChevronLeft, LogOut, Wand2 } from 'lucide-react';
import { CreateDialog } from './CreateDialog';
import { UploadRemixDialog } from './UploadRemixDialog';
import { DescribeDialog } from './DescribeDialog';
import { useAuth } from '../../context/AuthContext';

export const IdeogramLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isRemixDialogOpen, setIsRemixDialogOpen] = useState(false);
  const [isDescribeDialogOpen, setIsDescribeDialogOpen] = useState(false);
  const [initialPrompt, setInitialPrompt] = useState('');
  const { userEmail, logout } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Set page title based on current route
  useEffect(() => {
    const path = location.pathname;
    let title = 'Ideogram PromptStorm';
    
    if (path === '/ideogram' || path === '/ideogram/gallery') {
      title = 'Ideogram PromptStorm';
    } else if (path.startsWith('/ideogram/prompts')) {
      title = 'Prompts | Ideogram PromptStorm';
    }
    
    setPageTitle(title);
  }, [location.pathname]);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const userInitials = userEmail
    ?.split('@')[0]
    ?.split('.')
    ?.map((n: string) => n[0])
    ?.join('')
    ?.toUpperCase() || '??';

  const refreshGallery = () => {
    // Trigger a refresh in the Gallery component through its polling mechanism
    console.log('Gallery refresh requested');
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <div className={`${isCollapsed ? 'w-16' : 'w-64'} bg-white border-r transition-all duration-300 ease-in-out relative flex flex-col`}>
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="absolute -right-3 top-6 bg-white border rounded-full p-1 shadow-sm hover:bg-gray-50"
        >
          <ChevronLeft className={`h-4 w-4 text-gray-600 transition-transform duration-300 ${isCollapsed ? 'rotate-180' : ''}`} />
        </button>

        <div className="border-b flex justify-center items-center h-46">
          {isCollapsed ? (
            <Image className="h-12 w-12 text-blue-500 mb-1 mt-1 transition-all duration-300" />
          ) : (
            <div className="flex flex-col items-center justify-center py-8">
              <Image className="h-20 w-20 text-blue-500 mb-2" />
              <h1 className="text-xl font-bold text-gray-900">Ideogram</h1>
              <p className="text-sm text-gray-500">PromptStorm</p>
            </div>
          )}
        </div>

        <nav className="flex-1 mt-4">
          <Link
            to="/ideogram/gallery"
            className={`flex items-center px-4 py-2 text-sm ${
              isActive('/ideogram/gallery') ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Image className={`${isCollapsed ? '' : 'mr-3'} h-5 w-5`} />
            {!isCollapsed && 'Gallery'}
          </Link>

          <Link
            to="/ideogram/prompts"
            className={`flex items-center px-4 py-2 text-sm ${
              isActive('/ideogram/prompts') ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <List className={`${isCollapsed ? '' : 'mr-3'} h-5 w-5`} />
            {!isCollapsed && 'Prompts'}
          </Link>

          <button
            onClick={() => setIsCreateDialogOpen(true)}
            className="w-full flex items-center px-4 py-2 text-sm text-blue-600 hover:bg-gray-50"
          >
            <PlusCircle className={`${isCollapsed ? '' : 'mr-3'} h-5 w-5`} />
            {!isCollapsed && 'Create'}
          </button>

          <button
            onClick={() => setIsRemixDialogOpen(true)}
            className="w-full flex items-center px-4 py-2 text-sm text-blue-600 hover:bg-gray-50"
          >
            <Wand2 className={`${isCollapsed ? '' : 'mr-3'} h-5 w-5`} />
            {!isCollapsed && 'Remix'}
          </button>

          <button
            onClick={() => setIsDescribeDialogOpen(true)}
            className="w-full flex items-center px-4 py-2 text-sm text-blue-600 hover:bg-gray-50"
          >
            <Image className={`${isCollapsed ? '' : 'mr-3'} h-5 w-5`} />
            {!isCollapsed && 'Describe'}
          </button>

          <Link
            to="/apps"
            className="flex items-center px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
          >
            <Home className={`${isCollapsed ? '' : 'mr-3'} h-5 w-5`} />
            {!isCollapsed && 'Apps Hub'}
          </Link>
        </nav>

        <div className={`border-t p-4 ${isCollapsed ? 'items-center' : ''}`}>
          {!isCollapsed && (
            <>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-500">{userEmail}</span>
              </div>
              <div className="flex items-center justify-between">
                <button 
                  onClick={() => {
                    logout();
                    navigate('/');
                  }}
                  className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-2"
                >
                  <LogOut className="h-4 w-4" />
                  Logout
                </button>
                <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                  <span className="text-sm font-medium text-blue-600">{userInitials}</span>
                </div>
              </div>
            </>
          )}
          {isCollapsed && (
            <div className="flex flex-col items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                <span className="text-sm font-medium text-blue-600">{userInitials}</span>
              </div>
              <button 
                onClick={() => {
                  logout();
                  navigate('/');
                }}
                className="p-2 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-auto bg-gray-50">
        <Outlet />
      </div>

      <CreateDialog
        open={isCreateDialogOpen}
        onOpenChange={(open) => {
          setIsCreateDialogOpen(open);
          if (!open) {
            setInitialPrompt('');
          }
        }}
        onStartGenerating={refreshGallery}
        refreshGallery={refreshGallery}
        initialPrompt={initialPrompt}
      />

      <UploadRemixDialog
        open={isRemixDialogOpen}
        onOpenChange={setIsRemixDialogOpen}
        onStartGenerating={refreshGallery}
      />

      <DescribeDialog
        open={isDescribeDialogOpen}
        onOpenChange={setIsDescribeDialogOpen}
        onGenerateWithPrompt={(prompt) => {
          setInitialPrompt(prompt);
          setIsDescribeDialogOpen(false);
          setIsCreateDialogOpen(true);
        }}
      />
    </div>
  );
};
